/**
 * SafetyEnvelopeDiagram — the envelope picture for Immunity Closure
 * Guarantee explainers.
 *
 * Draws the primal Neutralization Theorem (or Dual Pair Guarantee) on one
 * side, the dual Structural Duality Principle on the other, and the
 * adjunction unit (η) and counit (ε) drawn as the closure that seals the
 * two sides into a single two-sided certificate.
 *
 * The two component colors are blended into the enclosing envelope so an
 * ICG card reads as visually distinct from a bare NT or DPG card: the
 * primal accent tints the left panel, the dual accent tints the right, and
 * the envelope backdrop carries a gradient between them.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React from "react";
import { FONT, GRAY, withAlpha } from "../../design-system/tokens.js";

export interface SafetyEnvelopeDiagramProps {
  /** ICG code, e.g. "ICG-Tr". */
  readonly icg: string;
  /** Primal side label, e.g. "DATA.LINEAGE" or "PRECOMMIT". */
  readonly primalLabel: string;
  /** Primal side kicker, e.g. "Dual Pair Guarantee · D1+D2". */
  readonly primalKind: string;
  /** Primal accent color (hex). */
  readonly primalAccent: string;
  /** Dual side label, e.g. "TRACE". */
  readonly dualLabel: string;
  /** Dual side kicker, e.g. "Structural Duality Principle · Tr". */
  readonly dualKind: string;
  /** Dual accent color (hex). */
  readonly dualAccent: string;
  /** The adjunction joining the two sides, e.g. "fibration ⊣ opfibration". */
  readonly adjunction: string;
  /** What the unit witnesses, e.g. "provenance witness". */
  readonly unitRole: string;
  /** What the counit witnesses, e.g. "reach receipt". */
  readonly counitRole: string;
}

function SidePanel({
  label,
  kind,
  accent,
  side,
}: {
  readonly label: string;
  readonly kind: string;
  readonly accent: string;
  readonly side: "primal" | "dual";
}): React.JSX.Element {
  return (
    <div
      style={{
        flex: "1 1 0",
        minWidth: "8rem",
        padding: "0.5rem 0.625rem",
        backgroundColor: withAlpha(accent, "1A"),
        border: `1px solid ${withAlpha(accent, "55")}`,
        borderRadius: "0.375rem",
      }}
    >
      <div
        style={{
          fontSize: "0.625rem",
          fontWeight: 700,
          color: accent,
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          marginBottom: "0.125rem",
        }}
      >
        {side === "primal" ? "Primal" : "Dual"}
      </div>
      <div style={{ fontWeight: 700, color: GRAY[900], fontSize: "0.9375rem" }}>{label}</div>
      <div style={{ color: GRAY[600], fontSize: "0.75rem" }}>{kind}</div>
    </div>
  );
}

export const SafetyEnvelopeDiagram = React.memo(function SafetyEnvelopeDiagram({
  icg,
  primalLabel,
  primalKind,
  primalAccent,
  dualLabel,
  dualKind,
  dualAccent,
  adjunction,
  unitRole,
  counitRole,
}: SafetyEnvelopeDiagramProps): React.JSX.Element {
  return (
    <figure
      aria-label={`${icg} safety envelope: ${primalLabel} closed with ${dualLabel} by the adjunction ${adjunction}`}
      style={{
        margin: 0,
        padding: "0.75rem",
        borderRadius: "0.5rem",
        border: `2px solid ${GRAY[300]}`,
        backgroundImage: `linear-gradient(135deg, ${withAlpha(primalAccent, "14")} 0%, ${withAlpha(
          dualAccent,
          "14",
        )} 100%)`,
      }}
    >
      <figcaption
        style={{
          fontSize: "0.625rem",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.08em",
          color: GRAY[600],
          marginBottom: "0.5rem",
          display: "flex",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <span>{icg} · Safety Envelope</span>
        <span style={{ fontWeight: 600, textTransform: "none", letterSpacing: 0 }}>
          <code>{adjunction}</code>
        </span>
      </figcaption>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "0.5rem",
          flexWrap: "wrap",
        }}
      >
        <SidePanel label={primalLabel} kind={primalKind} accent={primalAccent} side="primal" />

        <div
          style={{
            flex: "1 1 9rem",
            minWidth: "9rem",
            display: "flex",
            flexDirection: "column",
            gap: "0.25rem",
            fontFamily: FONT.mono,
            fontSize: "0.75rem",
            color: GRAY[700],
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", gap: "0.25rem" }}>
            <span style={{ color: primalAccent, fontWeight: 700 }}>η ▸</span>
            <span>unit · {unitRole}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", gap: "0.25rem" }}>
            <span>counit · {counitRole}</span>
            <span style={{ color: dualAccent, fontWeight: 700 }}>◂ ε</span>
          </div>
          <div style={{ textAlign: "center", color: GRAY[500], fontSize: "0.6875rem" }}>
            triangle identities seal the closure
          </div>
        </div>

        <SidePanel label={dualLabel} kind={dualKind} accent={dualAccent} side="dual" />
      </div>
    </figure>
  );
});
